import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from './cn';

/**
 * The text field. A faithful port of the CSS Module it replaced — same
 * --radius-md box, same padding, same focus halo — so it still lines up with
 * Select and SearchInput, which share those metrics.
 *
 * `invalid` sets `aria-invalid`, and every style keys off that ATTRIBUTE
 * rather than off the prop, so a field marked invalid by a form library that
 * writes the attribute directly still turns red without being told twice.
 * Never signal an error with the border alone: pair it with `FormError` beside
 * the field, which is what screen readers and colour-blind users actually get.
 */
export const inputVariants = [
  'block w-full min-w-0 rounded-[var(--radius-md)] border',
  'border-[color:var(--input)] bg-[var(--surface-elevated)]',
  // Height and inline padding come from the shared control metrics, not from
  // this file's own padding stack — see --control-h in tokens.css.
  'min-h-[var(--control-h)] px-[var(--control-px)] py-[var(--space-2)]',
  // 44px floor on touch — the shared control height lands near it; this is the
  // guarantee, not the design. Carried over from Input.module.css, which the
  // shadcn conversion deleted, when master's phase-171 touch pass merged in.
  'coarse:min-h-[var(--tap-min)]',
  'text-[length:var(--text-sm)] leading-[var(--leading-control)] tracking-[var(--tracking-snug)]',
  'text-[color:var(--text-primary)] shadow-[var(--shadow-xs)]',
  'transition-[border-color,box-shadow,background-color] duration-[var(--duration-fast)] ease-[var(--ease-out)]',
  'placeholder:text-[color:var(--text-muted)]',
  'hover:not-disabled:not-aria-invalid:border-[color:var(--border-strong)]',
  // One ring, defined once in tokens.css, shared with Select and PhoneField.
  'focus-visible:border-[color:var(--brand-600)] focus-visible:shadow-[var(--ring-field)]',
  // A disabled field is dashed as well as dimmed — shape survives a
  // screenshot, a greyscale print and a low-contrast display; colour does not.
  'disabled:cursor-not-allowed disabled:border-dashed',
  'disabled:bg-[var(--surface-subtle)] disabled:text-[color:var(--text-muted)]',
  'aria-invalid:border-[color:var(--danger-fg)] aria-invalid:bg-[var(--danger-bg)]',
  'aria-invalid:focus-visible:shadow-[var(--ring-field-danger)]',
  // Below 16px, focusing an input makes iOS Safari zoom the whole page. This
  // is the primitive nearly every form is built on, so the fix lives here once
  // rather than in each feature.
  'below-md:text-[length:var(--text-base)]',
  'file:mr-3 file:border-0 file:bg-transparent file:font-medium',
].join(' ');

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, type = 'text', ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      aria-invalid={invalid || undefined}
      className={cn(inputVariants, className)}
      {...rest}
    />
  );
});
